<script>
   /****************************************************************/
   /* PlotROC.                                                     */
   /* --------------------------------------------------------     */
   /* shows receiver operator curve for classification results     */
   /****************************************************************/

   import { Vector, vector, c } from 'mdatools/arrays';
   import { qnorm } from 'mdatools/distributions';

   import { Segments, Points, TextLabels } from 'svelte-plots-basic/2d';
   import { LINE_COLORS } from '@mdatools-apps/shared/plots/Defaults.js';
   import { plsres_get } from '@mdatools-apps/wasm/pls.js';
   import { csvRow, downloadCSV } from '@mdatools-apps/shared/utils';

   import PlotSeries from '@mdatools-apps/shared/plots/PlotSeries.svelte';
   import Axes from '@mdatools-apps/shared/plots/Axes.svelte';

   let {
      params,                        // JSON with main plot parameters
      props,                         // JSON with main properties (models, results, datasets, etc. formed in AppPlots)
   } = $props()

   // constants
   const fileName='plot-roc';
   const limX = [-0.1, 1.05];
   const limY = [-0.05, 1.1];


   // get necessary properties
   const resID = $derived(params ? params.resID : null);
   const model = $derived(props ? props.classmodel : null);
   const classNames = $derived(model ? model.classNames : null);
   const res = $derived(props && resID && props.res ? props.res[resID] : null);
   const classres = $derived(props && resID && props.classres ? props.classres[resID] : null);

   // number of points in the ROC curve
   const alpha = c(vector([0.005]), Vector.seq(0.01, 0.99, 0.01), vector([0.995]));

   const crit_soft = qnorm(alpha);
   const crit_hard = Vector.seq(1, -1, -0.02);

   // derived shared properties
   const yp = $derived(res ? plsres_get(res, 'Yp', model.ncomp, 1) : null);

   function getHard(yp, crit, ind) {

      const x = Vector.zeros(crit.length);
      const y = Vector.zeros(crit.length);

      const c0 = classNames[0];
      const c1 = classNames[1];

      const ind_members = ind[c0];
      const ind_strangers = ind[c1];

      let n = 0;
      for (let c of crit.v) {

         // members
         let TP = 0;
         for (let i of ind_members) {
            TP += yp[i] > c;
         }
         y.v[n] += TP;

         // strangers
         let TN = 0;
         for (let i of ind_strangers) {
            TN += yp[i] <= c;
         }
         x.v[n] += TN;
         n += 1;
      }

      const x_scalar = 1.0 / ind[c1].length;
      const y_scalar = 1.0 / ind[c0].length;
      return [x, y, x_scalar, y_scalar, crit]
   }


   function getSoft(yp, crit, ind, limType, ncomp) {

      const x = Vector.zeros(crit.length);
      const y = Vector.zeros(crit.length);

      for (let ic of [0, 1]) {

         const p = model.params[classNames[ic]][limType];
         const m = p.m[ncomp - 1];
         const s = p.s[ncomp - 1];

         const compfun = ic == 0 ? (x, c) => x >= c : (x, c) => x < c

         const ind_members = ind[classNames[ic]];
         const ind_strangers = ind[classNames[1 - ic]];

         let n = 0;
         let sign = ic == 0 ? -1 : 1;
         for (let c of crit.v) {

            let cy = m + sign * c * s;

            // members
            let TP = 0;
            for (let i of ind_members) {
               TP += compfun(yp[i], cy);
            }
            y.v[n] += TP;

            // strangers
            let TN = 0;
            for (let i of ind_strangers) {
               TN += !compfun(yp[i], cy);
            }

            x.v[n] += TN;
            n += 1;
         }
      }

      const scalar = 1.0 / yp.length;
      return [x, y, scalar, scalar, alpha];
   }


   function get(yp, rule, limType, ncomp, classres) {

      const crit = rule === 'hard' ? crit_hard : crit_soft;

      const [x, y, x_scalar, y_scalar, p] = rule === 'hard' ?
         getHard(yp, crit, classres.ind) :
         getSoft(yp, crit, classres.ind, limType, ncomp);

      let i_best = 0;
      let d_best = 2;
      for (let i = 1; i < x.length - 1; i++) {
         x.v[i] = 1 - x.v[i] * x_scalar;
         y.v[i] = y.v[i] * y_scalar;

         const d = x.v[i]**2 + (1 - y.v[i])**2
         if (d < d_best) {
            d_best = d;
            i_best = i;
         }
      }

      x.v[0] = 0;
      y.v[0] = 0;
      x.v[crit.length - 1] = 1;
      y.v[crit.length - 1] = 1;

      let auc = 0;
      for (let i = 0; i < x.length - 1; i++) {
         const dx = Math.abs(x.v[i + 1] - x.v[i]);
         auc += (y.v[i] + y.v[i + 1]) * dx / 2;
      }

      const xBest = x.v[i_best];
      const yBest = y.v[i_best];
      const aBest = p.v[i_best];
      return {x, y, auc, alpha, xBest, yBest,
         aBest: rule === 'soft' ? `⍺ = ${((1 - aBest)*100).toFixed(0)}%` : `τ = ${(aBest).toFixed(2)}`
      };
   }

   const ps = $derived(get(yp, model.rule, model.limType, model.ncomp, classres));

   // other plot parameters
   const title = $derived(model.ncomp ? `ROC (A = ${model.ncomp}, AUC = ${ps.auc.toFixed(4)})` : '');
   const curID = $derived(model.rule === 'soft' ? 'total' : classNames[0])
   // the hard rule's curve is the first class's, the soft rule's the totals
   const xLabel = $derived('1 – Specificity (' + curID + ')');
   const yLabel = $derived('Sensitivity (' + curID + ')');
   const curSens = $derived(classres.v[curID].stat.sensitivity[model.ncomp - 1]);
   const curSpec = $derived(classres.v[curID].stat.specificity[model.ncomp - 1]);

   // method for creating CSV version of the plot data: the points of the curve
   function downloadCSVCallback() {
      const { sep, dec } = props.data[resID];
      let out = csvRow([xLabel, yLabel], sep, dec);
      for (let i = 0; i < ps.x.length; i++) out += csvRow([ps.x.v[i], ps.y.v[i]], sep, dec);
      downloadCSV(out, fileName);
   }
</script>

{#if ps }

<Axes {title} {xLabel} {yLabel} {limX} {limY} {fileName} {downloadCSVCallback}>

   <Segments xStart={[0]} xEnd={[1]} yStart={[0]} yEnd={[1]} lineColor='#a0a0a0' lineType={3}/>


   <!-- data points with statistics -->
   <PlotSeries
      xValues={ps.x}
      yValues={ps.y}
      plotType='l'
      showLabels={false}
      mainColor={LINE_COLORS[resID]}
      lineWidth={1.5}
   />


   <!-- best alpha -->
   <Points marker={5} xValues={[ps.xBest]} yValues={[ps.yBest]} lineColor={LINE_COLORS[params.resID]} lineWidth={2} faceColor="yellow"/>
   <TextLabels xValues={[ps.xBest]} yValues={[ps.yBest]} labels={[ps.aBest]} pos={3} textSize={0.8}/>

   <!-- current alpha  -->
   <Points xValues={[1 - curSpec]} yValues={[curSens]} lineColor={LINE_COLORS[params.resID]} lineWidth={2} faceColor={LINE_COLORS[params.resID]}/>

</Axes>
{/if}